import {useCallback, useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {useAuthClient} from '../../../../utils/use-auth-client'
import {selectEffectiveRole} from '../../../../app-store/selector'
import {
  hasInfluencerOpsRole,
  hasPartnerOpsRole,
} from '../../../../utils/account-management-navigation'
// Same rule as the Scheduling-step gate (shouldRequireOnboardingCall) and the
// Proposal Tool — a location that qualifies for Scheduling always sends the
// codes that qualified it, never an empty productGroupCodes list.
import {
  buildProductGroupCodes,
  CALL_TYPES,
} from '../../../../utils/scheduling-call-rules'
import {buildUpdateOrderPayload} from '../utils/buildUpdateOrderPayload'

dayjs.extend(utc)
dayjs.extend(timezone)

// Ported from ProposalTool's ClientProposalSchedule (available-times fetch +
// book/cancel via plans/onboarding) — same backend calls, confirmed to work
// under a normal authenticated rep client (both routes sit behind the plain
// `auth` middleware, not gated to proposal-link tokens). Re-homed onto
// PurchaseLinkSlice location data instead of ProposalSlice, and onto the
// order-capture scheduled_appointment persistence (see
// useScheduledAppointments) instead of the proposals table.

function formatSlotTime(startTime, tzId) {
  try {
    return tzId
      ? dayjs(startTime).utc().tz(tzId).format('h:mm A')
      : dayjs(startTime).format('h:mm A')
  } catch {
    return dayjs(startTime).format('h:mm A')
  }
}

// Every scheduled_appointment row this flow creates/reads is tagged via the
// `interaction` column — the table has no location column, and Parent Pay
// combines every qualifying location into ONE order_id, so this is what lets
// us tell which row belongs to which location. Confirmed safe to repurpose:
// every other existing caller of this table (book-appointments.js,
// new-order.js, upgrade-order.js, ...) just writes a static, unparsed
// 'call Interaction' filler here — nothing reads it back for meaning.
const LOCATION_TAG_PREFIX = 'purchase-link-location:'

export function locationInteractionTag(locationId) {
  return `${LOCATION_TAG_PREFIX}${locationId}`
}

export function parseLocationIdFromInteraction(interaction) {
  if (!interaction?.startsWith(LOCATION_TAG_PREFIX)) return null
  return interaction.slice(LOCATION_TAG_PREFIX.length)
}

// Which named call a scheduled_appointment row belongs to — read back from
// the `appointment_type` column this flow writes ('onboarding' |
// 'thryv-success-services', same values legacy book-appointments.js
// captures). Anything else/unknown is treated as the kickoff call so rows
// booked before the TSS split still map somewhere sensible.
export function callTypeForAppointmentRow(row) {
  return (
    Object.values(CALL_TYPES).find(
      ct => ct.appointmentTypeKey === row?.appointment_type,
    ) ?? CALL_TYPES.kickoff
  )
}

// Fetches every scheduled_appointment row for this order ONCE (shared across
// all of an order's locations, since Parent Pay locations share one
// order_id) and maps the latest non-cancelled row per location AND per named
// call: appointmentsByLocationId[locationId][callKey] → row.
export function useScheduledAppointments(orderId) {
  const client = useAuthClient()
  const [byLocationId, setByLocationId] = useState({})
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(() => {
    if (!orderId) {
      setLoading(false)
      return
    }
    setLoading(true)
    client(`ordercapture/services/getscheduledAppointments/${orderId}`)
      .then(res => {
        const rows = res?.response ?? []
        const map = {}
        rows
          .filter(row => row.schedule_status !== 'Cancelled')
          .forEach(row => {
            const locationId = parseLocationIdFromInteraction(row.interaction)
            if (!locationId) return
            const callKey = callTypeForAppointmentRow(row).key
            // Rows have no reliable ordering column to prefer besides id
            // (auto-increment) — later id wins if a call was rebooked.
            const existing = map[locationId]?.[callKey]
            if (!existing || (row.id ?? 0) > (existing.id ?? 0)) {
              map[locationId] = {...(map[locationId] ?? {}), [callKey]: row}
            }
          })
        setByLocationId(map)
      })
      .catch(() => setByLocationId({}))
      .finally(() => setLoading(false))
  }, [orderId, client])

  useEffect(() => {
    refetch()
  }, [refetch])

  return {appointmentsByLocationId: byLocationId, loading, refetch}
}

const COUNTRY_FLAGS = {
  AU: {isAus: true, isNZ: false},
  NZ: {isAus: false, isNZ: true},
}

// Per-location calendar/slot-picking + book/cancel engine.
// location: a PurchaseLinkSlice location object ({locationId, country, items, ...})
// accountInfo/orderId: PurchaseLinkSlice.accountInfo / orderId
// callType: one of CALL_TYPES (scheduling-call-rules.js) — which named call
//   this instance books; defaults to the kickoff call
// existingAppointment: the row from useScheduledAppointments for this
//   location + callType, or null
// onboardingContact: {firstName, lastName, email, mobile} — who the
//   call-booking API should treat as the appointment's contact (see
//   handleScheduleCall's plans/onboarding payload). Ports the old flow's
//   onboarding-program screen's "Onboarding Contact" fields / "Same as
//   account owner" checkbox (SchedulingStep resolves this before passing it
//   down, falling back to accountInfo when the account owner is used).
// currentAppointments: appointmentsByLocationId[location.locationId] from
//   useScheduledAppointments — {[callKey]: row}. Read (not written) here, to
//   know the OTHER call's current appt id whenever this one changes, since
//   order_header's appt-id columns are set together on every sync (see
//   syncOrderHeaderAppointment / buildUpdateOrderPayload).
// languagePreference/region: 'es'|'en' and the sales-region string from the
//   Kickoff call's Spanish toggle / Region select (see
//   AccountSetup/utils/schedulingLanguageRules.js) — both default to the
//   legacy no-op values so non-kickoff callers are unaffected.
export function useOnboardingScheduling({
  location,
  accountInfo,
  orderId,
  callType = CALL_TYPES.kickoff,
  existingAppointment,
  onboardingContact,
  currentAppointments,
  languagePreference = 'en',
  region = '',
  onScheduled,
  onCancelled,
}) {
  const client = useAuthClient()
  // The legacy PartnerOps scheduler always booked as a Keap-client portal
  // request (appointment hold tagged 'keap', created by KeapClient).
  const effectiveRole = useSelector(selectEffectiveRole)
  const isKeapClientPortalReq = hasPartnerOpsRole(effectiveRole)
  // Same flag ooecp-app sends: available-times keys the service-id pool off it
  // (it also covers a rep switched into the role, where auth.user_roles[0]
  // isn't InfluencerOps).
  const isInfluencerOps = hasInfluencerOpsRole(effectiveRole)
  const abortRef = useRef(null)
  // Booking/cancelling both await two sequential network calls — the parent's
  // onScheduled/onCancelled callback triggers a refetch that can re-render
  // this card (and its sibling cards, since useScheduledAppointments lives
  // one level up) before the second await resolves. Guards every setState
  // after an await against that.
  const isMountedRef = useRef(true)
  useEffect(
    () => () => {
      isMountedRef.current = false
    },
    [],
  )

  const [selectedDate, setSelectedDate] = useState(null)
  const [slots, setSlots] = useState([])
  const [selectedSlot, setSelectedSlot] = useState(null)
  const [timeZoneId, setTimeZoneId] = useState(null)
  const [serviceId, setServiceId] = useState(null)
  const [loadingSlots, setLoadingSlots] = useState(false)
  const [scheduling, setScheduling] = useState(false)
  const [cancelling, setCancelling] = useState(false)
  const [scheduleError, setScheduleError] = useState(null)

  const country = location?.country || accountInfo?.country || 'US'

  // available-times resolves the appointment slots' timezone by geocoding
  // `address+country` (see cc-order-service.js's getAvailableTimes) — with no
  // address it geocodes the country alone, which Google resolves to a point
  // near the country's geographic centroid (America/Denver for the US) rather
  // than the customer's actual timezone, silently mis-booking/mis-displaying
  // every call by however many hours separate the two zones. Neither
  // `location` nor `accountInfo` in PurchaseLinkSlice carry the street
  // address (only identity/billing fields — see initialAccountInfo), so
  // resolve it from the account's own record instead, once per location,
  // mirroring ProposalTool's ClientProposalSchedule (recurlyAddr.street1/
  // city/region) which this hook was ported from.
  const [resolvedAddress, setResolvedAddress] = useState('')
  const resolvedForThryvIdRef = useRef(null)
  useEffect(() => {
    const thryvId = location?.thryvId
    if (!thryvId || resolvedForThryvIdRef.current === thryvId) return
    resolvedForThryvIdRef.current = thryvId
    setResolvedAddress('')
    client(`accounts/account-details/${thryvId}`)
      .then(data => {
        if (!isMountedRef.current) return
        const addr = data?.account?.address
        const parts = [addr?.street1, addr?.city, addr?.region].filter(Boolean)
        setResolvedAddress(parts.join(', '))
      })
      .catch(() => {})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.thryvId])

  const fetchSlots = useCallback(
    date => {
      if (abortRef.current) abortRef.current.abort()
      const controller = new AbortController()
      abortRef.current = controller
      // A newer fetchSlots call aborts this one; its then/catch/finally must
      // not touch state, or the aborted call's finally clears the spinner
      // while the newer request is still in flight.
      const isStale = () =>
        !isMountedRef.current || abortRef.current !== controller
      setLoadingSlots(true)
      setSlots([])
      setSelectedSlot(null)

      const dateStr = dayjs(date).format('YYYY-MM-DD')
      const codes = buildProductGroupCodes(location?.items)
      const url =
        `cc/order/available-times?appointmentType=${callType.appointmentType}` +
        `&country=${country}` +
        `&languagePreference=${languagePreference}` +
        `&date=${dateStr}&address=${encodeURIComponent(
          resolvedAddress,
        )}&productGroupCodes=${codes.join(',')}` +
        (region ? `&region=${encodeURIComponent(region)}` : '') +
        (isInfluencerOps ? '&isInfluencerOps=true' : '')

      client(url, {signal: controller.signal})
        .then(data => {
          if (isStale()) return
          const tz = data?.timeZone?.timeZoneId || null
          const svcId = data?.serviceIdTitle?.serviceId || null
          const raw = data?.availableTimes || []
          setTimeZoneId(tz)
          setServiceId(svcId)
          setSlots(
            raw.map(slot => ({
              ...slot,
              serviceId: slot.serviceId ?? svcId,
              displayTime: formatSlotTime(slot.startTime, tz),
            })),
          )
        })
        .catch(err => {
          if (err?.name !== 'AbortError' && !isStale()) setSlots([])
        })
        .finally(() => {
          if (!isStale()) setLoadingSlots(false)
        })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      country,
      location,
      resolvedAddress,
      callType.appointmentType,
      languagePreference,
      region,
      isInfluencerOps,
    ],
  )

  // Mirrors this call's new appt id (or null, on reset) onto order_header —
  // best-effort: scheduled_appointment (already updated by the caller before
  // this runs) stays the real source of truth for this screen either way,
  // so a failure here doesn't block or roll back the schedule/cancel action.
  // Always resends BOTH appt-id fields (reading the other one's current
  // value off currentAppointments) since updateOrder sets them together —
  // see buildUpdateOrderPayload for the Multi-Location Parent Pay caveat.
  async function syncOrderHeaderAppointment(newApptId) {
    if (!orderId) return
    const kickoffApptId =
      callType.key === 'kickoff'
        ? newApptId
        : currentAppointments?.kickoff?.appt_id ?? null
    const thryvSuccessServicesApptId =
      callType.key === 'thryv-success-services'
        ? newApptId
        : currentAppointments?.['thryv-success-services']?.appt_id ?? null

    try {
      // Read-modify-write — fetch the order fresh rather than rebuilding it
      // from Redux cart state, so every other column/line/discount round-
      // trips untouched (see buildUpdateOrderPayload for why).
      const getResult = await client(
        `ordercapture/services/getOrderDetails/${orderId}`,
      )
      const currentOrder = getResult?.response
      if (!currentOrder) {
        // eslint-disable-next-line no-console
        console.error(
          'syncOrderHeaderAppointment: getOrderDetails returned no order',
          getResult,
        )
        return
      }

      const payload = buildUpdateOrderPayload(currentOrder, {
        orderId,
        onboardingApptId: kickoffApptId,
        thryvSuccessServicesApptId,
        locationThryvId: location?.thryvId ?? null,
      })

      const updateResult = await client('ordercapture/services/updateOrder', {
        data: payload,
        method: 'POST',
      })
      if (!updateResult?.Success && !updateResult?.success) {
        // eslint-disable-next-line no-console
        console.error(
          'syncOrderHeaderAppointment: updateOrder returned failure',
          updateResult,
        )
      }
    } catch (err) {
      // Swallowed (best-effort, see comment above) but still logged —
      // scheduled_appointment (already updated by the caller) stays correct
      // either way, so this should never silently hide a real problem.
      // eslint-disable-next-line no-console
      console.error('syncOrderHeaderAppointment failed', err)
    }
  }

  function handleDateChange(date) {
    setSelectedDate(date)
    setScheduleError(null)
    fetchSlots(date)
  }

  // Re-fetch (or, with no date picked yet, just clear any stale selection)
  // whenever the Spanish toggle or Region select changes — mirrors legacy
  // date-time-modal.js's onLanguagePreferenceChange / region-change effect,
  // which both re-query available-times instead of leaving slots fetched
  // under the previous language/region showing.
  useEffect(() => {
    if (selectedDate) {
      fetchSlots(selectedDate)
    } else {
      setSlots([])
      setSelectedSlot(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [languagePreference, region])

  async function handleScheduleCall() {
    if (!selectedSlot || scheduling) return
    setScheduling(true)
    setScheduleError(null)

    const countryFlags = COUNTRY_FLAGS[country] || {isAus: false, isNZ: false}
    const onboardingPayload = {
      timeZone: timeZoneId,
      serviceId,
      staffId: selectedSlot.staffId,
      startTime: selectedSlot.startTime,
      thryvBookingDate: selectedSlot.startDateTime || selectedSlot.startTime,
      country,
      ...countryFlags,
      isApptHold: true,
      languagePreference,
    }

    try {
      const {apptId} = await client('plans/onboarding', {
        data: {
          firstName:
            onboardingContact?.firstName || accountInfo?.firstName || '',
          lastName: onboardingContact?.lastName || accountInfo?.lastName || '',
          email: onboardingContact?.email || accountInfo?.email || '',
          phone:
            onboardingContact?.mobile ||
            accountInfo?.mobile ||
            accountInfo?.phone ||
            '',
          company: location?.locationName || accountInfo?.businessName || '',
          onboarding: onboardingPayload,
          ...(isKeapClientPortalReq && {isKeapClientPortalReq: true}),
        },
      })

      // Persist the call's date/time in the CALL's own zone (the zone the
      // slots were offered in), not the raw UTC instant: available-times
      // returns `startTime` as an ISO UTC string and no `startDateTime`, so
      // splitting it on 'T' would store the UTC hour and the confirmation
      // strip would then label a UTC time as CST. Legacy stored local time
      // here too (its scheduler formatted `local_start_time` in the slot zone).
      const bookingInstant = dayjs(selectedSlot.startTime)
      let datePart
      let timePart
      if (bookingInstant.isValid() && timeZoneId) {
        const local = bookingInstant.tz(timeZoneId)
        datePart = local.format('YYYY-MM-DD')
        timePart = local.format('HH:mm:ss')
      } else {
        const bookingDate = String(onboardingPayload.thryvBookingDate ?? '')
        ;[datePart, timePart] = bookingDate.includes('T')
          ? [bookingDate.split('T')[0], bookingDate.split('T')[1].slice(0, 8)]
          : [dayjs(selectedDate).format('YYYY-MM-DD'), '00:00:00']
      }

      await client('ordercapture/services/createAppointment', {
        data: [
          {
            order_id: orderId,
            schedule_status: 'Scheduled',
            appointment_type: callType.appointmentTypeKey,
            timezone: timeZoneId,
            appt_id: apptId,
            date: datePart,
            time: timePart,
            title: `${callType.label} — ${
              location?.locationName || accountInfo?.businessName || ''
            }`,
            duration: 30,
            service_id: serviceId,
            staff_id: selectedSlot.staffId,
            interaction: locationInteractionTag(location.locationId),
            zoomURL: 'https://www.zoom.com',
          },
        ],
        method: 'POST',
      })

      await syncOrderHeaderAppointment(apptId)

      onScheduled?.({
        apptId,
        date: datePart,
        time: timePart,
        timezone: timeZoneId,
      })
      if (isMountedRef.current) setSelectedSlot(null)
    } catch (err) {
      if (isMountedRef.current) {
        setScheduleError(
          err?.message || 'Failed to schedule. Please try again.',
        )
      }
    } finally {
      if (isMountedRef.current) setScheduling(false)
    }
  }

  async function handleCancelSchedule() {
    if (!existingAppointment?.appt_id || cancelling) return
    setCancelling(true)
    try {
      // Legacy releases each call type through its own DELETE route
      // (book-appointments.js cancelAppointment's urls map) — same here.
      await client(callType.cancelEndpoint, {
        data: {
          type: callType.appointmentTypeKey,
          demoApptId: existingAppointment.appt_id,
          country,
        },
        method: 'DELETE',
      }).catch(() => {})
      await client('ordercapture/services/updateScheduledAppointmentStatus', {
        data: {
          appt_id: existingAppointment.appt_id,
          schedule_status: 'Cancelled',
        },
        method: 'PUT',
      })
      await syncOrderHeaderAppointment(null)
      onCancelled?.()
    } finally {
      if (isMountedRef.current) setCancelling(false)
    }
  }

  return {
    selectedDate,
    slots,
    selectedSlot,
    timeZoneId,
    loadingSlots,
    scheduling,
    cancelling,
    scheduleError,
    setSelectedSlot,
    handleDateChange,
    handleScheduleCall,
    handleCancelSchedule,
  }
}
